import { BigButton } from '@/components/BigButton';
import RadioInput from '@/components/Inputs/RadioInput';
import { useGiftsStore } from '@/hooks/useGiftsStore';
import { useUserProfile } from '@/hooks/useUserData';
import {
    getAvailableGifts,
    getGiftsUsage,
    useShoppingCartStore,
} from '@/store/shoppingCart/shoppingCartStore';
import { AnimatePresence } from 'framer-motion';
import { useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import animations from '../../helpers/animations';
import {
    BackgroundModal,
    CloseModalButton,
    GiftsModalForm,
    ModalContent,
    ModalWrapper,
} from './styles';
import { DateUtils } from '@/helpers/dateUtils';

export function GiftsModal() {
    const modalRef = useRef<HTMLDivElement | null>(null);
    const giftsStore = useGiftsStore();
    const userProfile = useUserProfile();
    const shoppingCartStore = useShoppingCartStore();
    const [currentSelected, setCurrentSelected] = useState<number | null>(null);

    function handleCloseModal() {
        setCurrentSelected(null);
        giftsStore.callback?.(null);
        giftsStore.update({
            isVisible: false,
            callback: null,
        });
    }

    const userAvailableGifts = useMemo(() => {
        if (!userProfile.data) return [];
        if (!userProfile.data.gifts?.length) return [];
        const notYetUsedGifts = getAvailableGifts(userProfile.data);

        return notYetUsedGifts;
    }, [userProfile.data?.gifts, shoppingCartStore.cartValues]);

    const giftsUsageCount = useMemo(() => {
        return getGiftsUsage(shoppingCartStore.cartValues ?? []);
    }, [userAvailableGifts]);

    const handleFinish = () => {
        const targetGift = userAvailableGifts.find(
            gift => gift.id === currentSelected
        );
        giftsStore.callback?.(targetGift ?? null);
        giftsStore.update({
            isVisible: false,
            callback: null,
        });
        setCurrentSelected(null);
    };

    return createPortal(
        <AnimatePresence>
            {giftsStore.isVisible && (
                <BackgroundModal
                    ref={modalRef}
                    onClick={handleCloseModal}
                    {...animations.fadeInOut(0.1)}
                >
                    <ModalWrapper onClick={e => e.stopPropagation()}>
                        <GiftsModalForm>
                            <div className="title">
                                <p>Escolha uma First Heat</p>
                            </div>
                            <CloseModalButton onClick={handleCloseModal} />
                            <ModalContent>
                                <div className="gifts-column">
                                    {userAvailableGifts.map(gift => {
                                        const availableCount =
                                            gift.availableAmount -
                                            (giftsUsageCount.get(gift.id) ?? 0);
                                        return (
                                            <RadioInput
                                                key={gift.id}
                                                currentSelected={
                                                    currentSelected
                                                }
                                                radioId={`gift-${gift.id}`}
                                                setCurrentSelected={
                                                    setCurrentSelected
                                                }
                                                text={
                                                    <p className="text-label">
                                                        {gift.giftType.name}{' '}
                                                        <span>
                                                            expira em{' '}
                                                            {DateUtils.formatSingleDate(
                                                                new Date(
                                                                    gift.expiresOn
                                                                )
                                                            )}
                                                            , {availableCount}{' '}
                                                            {availableCount === 1 ? "disponível" : "disponíveis"}
                                                        </span>
                                                    </p>
                                                }
                                                value={gift.id}
                                            />
                                        );
                                    })}
                                </div>
                                <div className="buttons-wrapper">
                                    <BigButton
                                        bgColor="secondary"
                                        color="secondary-alt"
                                        onClick={handleCloseModal}
                                    >
                                        Cancelar
                                    </BigButton>
                                    <BigButton
                                        bgColor={
                                            currentSelected === null
                                                ? 'secondary'
                                                : 'blue'
                                        }
                                        color={
                                            currentSelected === null
                                                ? 'secondary-alt'
                                                : 'white'
                                        }
                                        onClick={handleFinish}
                                        disabled={currentSelected === null}
                                        cursorBlocked={currentSelected === null}
                                    >
                                        Resgatar
                                    </BigButton>
                                </div>
                            </ModalContent>
                        </GiftsModalForm>
                    </ModalWrapper>
                </BackgroundModal>
            )}
        </AnimatePresence>,
        document.getElementById('root')!
    );
}
